import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { DemoLockNotice, DemoLockWrap } from "../components/DemoLock";

const KYC_STATUS_TONE = { verified: "text-leaf", review: "text-danger" };

const newUboKey = () => `ubo-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
const EMPTY_UBO = () => ({ _key: newUboKey(), full_name: "", nationality: "", date_of_birth: "", ownership_pct: 25, id_document_type: "passport", id_document_number: "", is_pep: false });
const EMPTY = {
  company_name: "", legal_form: "", country: "", registered_address: "", operating_address: "",
  date_of_incorporation: "", industry: "", website: "",
  vat_number: "", business_license_number: "", lei: "",
  signatory: { full_name: "", position: "", nationality: "", date_of_birth: "", id_document_type: "passport", id_document_number: "" },
  contact_email: "",
  ubos: [EMPTY_UBO()],
  source_of_funds: "operating revenue", expected_annual_volume_usd: 100000,
  pep_declaration: false, sanctions_declaration: false, aml_policy_acknowledged: false,
};

export default function KYC() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("unverified");

  useEffect(() => {
    api.get("/kyc/me").then((r) => {
      setStatus(r.data?.status || user?.kyc_status || "unverified");
      if (user?.email && !f.contact_email) setF((x) => ({ ...x, contact_email: user.email }));
    });
     
  }, [user]);

  const upd = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const updSig = (k) => (e) => setF((x) => ({ ...x, signatory: { ...x.signatory, [k]: e.target.value } }));
  const updUBO = (i, k, val) => setF((x) => {
    const ubos = [...x.ubos]; ubos[i] = { ...ubos[i], [k]: val }; return { ...x, ubos };
  });
  const addUBO = () => setF((x) => ({ ...x, ubos: [...x.ubos, EMPTY_UBO()] }));
  const rmUBO = (i) => setF((x) => ({ ...x, ubos: x.ubos.filter((_, j) => j !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.vat_number && !f.business_license_number) return toast.error("Provide VAT or Business License number.");
    if (!f.sanctions_declaration) return toast.error("Sanctions attestation is mandatory.");
    if (!f.aml_policy_acknowledged) return toast.error("Acknowledge the AML/CTF policy.");
    setBusy(true);
    try {
      const payload = {
        ...f,
        expected_annual_volume_usd: Number(f.expected_annual_volume_usd),
        ubos: f.ubos.map(({ _key, ...u }) => ({ ...u, ownership_pct: Number(u.ownership_pct) })),
      };
      const { data } = await api.post("/kyc/submit", payload);
      await refresh();
      toast.success(`KYB ${data.status} · risk: ${data.risk_rating}`);
      setStatus(data.status);
      if (data.status === "verified") setTimeout(() => nav("/marketplace"), 900);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-14 pb-24">
        <div className="label-tag mb-4">Corporate KYB · FinCEN CDD · EU 5AMLD · GCC FATF</div>
        <h1 className="font-serif text-5xl tracking-tight">Verify your company</h1>
        <p className="text-ink2 mt-3 max-w-3xl">
          GreenBelt is a B2B exchange. Onboarding follows the US Bank Secrecy Act (FinCEN Customer Due Diligence Rule),
          EU 5AMLD / 6AMLD, and GCC central bank guidance (UAE CB, SAMA, CBB) aligned with FATF Recommendations 10 &amp; 22.
        </p>

        <div className="mt-6 inline-block px-3 py-1 border border-line font-mono text-[11px] tracking-wider uppercase" data-testid="kyc-status">
          Status: <span className={KYC_STATUS_TONE[status] || "text-ink2"}>{status}</span>
        </div>

        <DemoLockNotice action="kyc" />
        <DemoLockWrap>
        <form onSubmit={submit} className="mt-10 space-y-10">
          <Block title="1 · Entity">
            <In label="Company legal name *" v={f.company_name} on={upd("company_name")} id="company_name" req />
            <In label="Legal form (LLC, Ltda, GmbH…) *" v={f.legal_form} on={upd("legal_form")} id="legal_form" req />
            <In label="Country of incorporation (ISO) *" v={f.country} on={upd("country")} id="country" req placeholder="US, BR, AE, DE…" />
            <In label="Date of incorporation *" v={f.date_of_incorporation} on={upd("date_of_incorporation")} id="incorp" type="date" req />
            <In label="Industry *" v={f.industry} on={upd("industry")} id="industry" req />
            <In label="Website" v={f.website} on={upd("website")} id="website" />
            <In label="Registered address *" v={f.registered_address} on={upd("registered_address")} id="registered_address" req full />
            <In label="Operating address (if different)" v={f.operating_address} on={upd("operating_address")} id="operating_address" full />
          </Block>

          <Block title="2 · Tax & Registration — VAT or Business License is mandatory">
            <In label="VAT number" v={f.vat_number} on={upd("vat_number")} id="vat_number" />
            <In label="Business License number (if no VAT)" v={f.business_license_number} on={upd("business_license_number")} id="business_license_number" />
            <In label="LEI (recommended for EU)" v={f.lei} on={upd("lei")} id="lei" full />
          </Block>

          <Block title="3 · Authorized signatory">
            <In label="Full name *" v={f.signatory.full_name} on={updSig("full_name")} id="sig_name" req />
            <In label="Position / title *" v={f.signatory.position} on={updSig("position")} id="sig_position" req />
            <In label="Nationality *" v={f.signatory.nationality} on={updSig("nationality")} id="sig_nat" req />
            <In label="Date of birth *" v={f.signatory.date_of_birth} on={updSig("date_of_birth")} id="sig_dob" type="date" req />
            <Sel label="ID document type *" v={f.signatory.id_document_type} on={updSig("id_document_type")} id="sig_idtype" opts={["passport", "national_id", "drivers_license"]} />
            <In label="ID document number *" v={f.signatory.id_document_number} on={updSig("id_document_number")} id="sig_idnum" req />
            <In label="Authorized contact email *" v={f.contact_email} on={upd("contact_email")} id="contact_email" type="email" req full />
          </Block>

          <Block title="4 · Ultimate Beneficial Owners (≥25%)" desc="Per FinCEN CDD & EU 5AMLD. If no individual holds ≥25%, declare a senior managing official.">
            <div className="md:col-span-2 space-y-4">
              {f.ubos.map((u, i) => (
                <div key={u._key || `ubo-${i}`} className="border border-line p-5 grid grid-cols-1 md:grid-cols-2 gap-4 bg-muted/20" data-testid={`ubo-${i}`}>
                  <In label="Full name *" v={u.full_name} on={(e) => updUBO(i, "full_name", e.target.value)} id={`ubo_name_${i}`} req />
                  <In label="Nationality *" v={u.nationality} on={(e) => updUBO(i, "nationality", e.target.value)} id={`ubo_nat_${i}`} req />
                  <In label="Date of birth *" v={u.date_of_birth} on={(e) => updUBO(i, "date_of_birth", e.target.value)} id={`ubo_dob_${i}`} type="date" req />
                  <In label="Ownership % *" v={u.ownership_pct} on={(e) => updUBO(i, "ownership_pct", e.target.value)} id={`ubo_pct_${i}`} type="number" req />
                  <Sel label="ID document type" v={u.id_document_type} on={(e) => updUBO(i, "id_document_type", e.target.value)} id={`ubo_idt_${i}`} opts={["passport", "national_id"]} />
                  <In label="ID document number *" v={u.id_document_number} on={(e) => updUBO(i, "id_document_number", e.target.value)} id={`ubo_idn_${i}`} req />
                  <label className="md:col-span-2 flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={u.is_pep} onChange={(e) => updUBO(i, "is_pep", e.target.checked)} data-testid={`ubo_pep_${i}`} />
                    This person is a Politically Exposed Person (PEP) or family/close associate of one.
                  </label>
                  {f.ubos.length > 1 && (
                    <button type="button" onClick={() => rmUBO(i)} className="text-xs font-mono text-danger underline justify-self-start">REMOVE</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addUBO} className="btn-outline !py-2 !px-4 !text-xs" data-testid="ubo-add">+ Add another beneficial owner</button>
            </div>
          </Block>

          <Block title="5 · AML / CTF declarations">
            <Sel label="Source of funds *" v={f.source_of_funds} on={upd("source_of_funds")} id="source_of_funds"
                opts={["operating revenue", "capital injection", "loan financing", "investment proceeds", "treasury", "other"]} />
            <In label="Expected annual volume (USD) *" v={f.expected_annual_volume_usd} on={upd("expected_annual_volume_usd")} id="expected_volume" type="number" req />
            <label className="md:col-span-2 flex items-start gap-3 text-sm border border-line p-4 bg-muted/20">
              <input type="checkbox" checked={f.pep_declaration} onChange={(e) => setF((x) => ({ ...x, pep_declaration: e.target.checked }))} data-testid="pep-declaration" className="mt-1" />
              <span>Any director, signatory or UBO is a <strong>Politically Exposed Person</strong> (current or within last 12 months).</span>
            </label>
            <label className="md:col-span-2 flex items-start gap-3 text-sm border border-line p-4 bg-muted/20">
              <input type="checkbox" checked={f.sanctions_declaration} onChange={(e) => setF((x) => ({ ...x, sanctions_declaration: e.target.checked }))} data-testid="sanctions-declaration" className="mt-1" required />
              <span>I attest the company and its UBOs are <strong>not on any sanctions list</strong> (US OFAC SDN, EU Consolidated, UN, UK HMT, or GCC).</span>
            </label>
            <label className="md:col-span-2 flex items-start gap-3 text-sm border border-line p-4 bg-muted/20">
              <input type="checkbox" checked={f.aml_policy_acknowledged} onChange={(e) => setF((x) => ({ ...x, aml_policy_acknowledged: e.target.checked }))} data-testid="aml-ack" className="mt-1" required />
              <span>I have read and accept GreenBelt's <strong>AML / CTF policy</strong>, including ongoing monitoring and source-of-funds checks.</span>
            </label>
          </Block>

          <div className="flex justify-end">
            <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50" data-testid="kyc-submit">
              {busy ? "Submitting…" : "Submit KYB →"}
            </button>
          </div>
        </form>
        </DemoLockWrap>
      </section>
    </Layout>
  );
}

function Block({ title, desc, children }) {
  return (
    <div className="border border-ink">
      <div className="p-5 border-b border-line bg-muted/30">
        <div className="font-serif text-2xl">{title}</div>
        {desc && <div className="text-sm text-ink2 mt-1">{desc}</div>}
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}
function In({ label, v, on, id, req, type = "text", full, placeholder }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="label-tag block mb-2">{label}</label>
      <input type={type} required={req} value={v || ""} onChange={on} placeholder={placeholder} className="input-field" data-testid={`kyc-${id}`} />
    </div>
  );
}
function Sel({ label, v, on, id, opts, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="label-tag block mb-2">{label}</label>
      <select value={v} onChange={on} className="input-field" data-testid={`kyc-${id}`}>
        {opts.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
      </select>
    </div>
  );
}
